import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  filters: {
    marginBottom: theme.spacing(2),
    alignItems: "flex-end"
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  total: {
    marginBottom: theme.spacing(1),
    fontWeight: "bold"
  },
  block: {
    marginBottom: theme.spacing(3)
  }
}));

const todayStr = () => new Date().toISOString().split("T")[0];
const monthAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split("T")[0];
};

const formatDateTime = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("pt-BR");
};

const ForbiddenWordsReport = () => {
  const classes = useStyles();

  const [dateFrom, setDateFrom] = useState(monthAgoStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [users, setUsers] = useState([]);
  const [terms, setTerms] = useState([]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forbidden-words/report", {
        params: {
          dateFrom,
          dateTo,
          ...(selectedTerm ? { term: selectedTerm } : {}),
          ...(selectedUserId ? { userId: selectedUserId } : {})
        }
      });
      setReport(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [dateFrom, dateTo, selectedTerm, selectedUserId]);

  // Carrega as opções dos filtros (atendentes e termos cadastrados).
  useEffect(() => {
    (async () => {
      try {
        const [usersRes, termsRes] = await Promise.all([
          api.get("/users/list"),
          api.get("/forbidden-words/list")
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setTerms(Array.isArray(termsRes.data) ? termsRes.data : []);
      } catch (err) {
        // Silencioso: filtros ficam vazios se falhar.
      }
    })();
  }, []);

  // Gera o relatório automaticamente ao abrir a tela (filtro padrão: último mês).
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("forbiddenWordsReport.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} className={classes.filters}>
          <Grid item>
            <TextField
              label={i18n.t("common.from")}
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label={i18n.t("common.to")}
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              select
              label={i18n.t("forbiddenWordsReport.fields.term")}
              value={selectedTerm}
              onChange={e => setSelectedTerm(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">{i18n.t("common.all")}</MenuItem>
              {terms.map(t => (
                <MenuItem key={t.id} value={t.term}>
                  {t.term}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item>
            <TextField
              select
              label={i18n.t("forbiddenWordsReport.fields.user")}
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">{i18n.t("common.all")}</MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchReport}
              disabled={loading}
            >
              {loading ? i18n.t("common.loading") : i18n.t("common.filter")}
            </Button>
          </Grid>
        </Grid>

        {report && (
          <>
            <Typography className={classes.total}>
              {i18n.t("forbiddenWordsReport.total", { total: report.total })}
            </Typography>

            <div className={classes.block}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {i18n.t("forbiddenWordsReport.byTerm")}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("forbiddenWordsReport.fields.term")}</TableCell>
                    <TableCell align="right">{i18n.t("forbiddenWordsReport.fields.occurrences")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byTerm.map(row => (
                    <TableRow key={row.term}>
                      <TableCell>{row.term}</TableCell>
                      <TableCell align="right">{row.total}</TableCell>
                    </TableRow>
                  ))}
                  {report.byTerm.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        {i18n.t("forbiddenWordsReport.noOccurrences")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className={classes.block}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {i18n.t("forbiddenWordsReport.byUser")}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("forbiddenWordsReport.fields.user")}</TableCell>
                    <TableCell align="right">{i18n.t("forbiddenWordsReport.fields.occurrences")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byUser.map(row => (
                    <TableRow key={`${row.userId}-${row.userName}`}>
                      <TableCell>{row.userName || "—"}</TableCell>
                      <TableCell align="right">{row.total}</TableCell>
                    </TableRow>
                  ))}
                  {report.byUser.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        {i18n.t("forbiddenWordsReport.noOccurrences")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className={classes.block}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {i18n.t("forbiddenWordsReport.details")}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("common.date")}</TableCell>
                    <TableCell>{i18n.t("forbiddenWordsReport.fields.term")}</TableCell>
                    <TableCell>{i18n.t("forbiddenWordsReport.fields.user")}</TableCell>
                    <TableCell align="right">{i18n.t("forbiddenWordsReport.fields.ticket")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.details.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell>{row.term}</TableCell>
                      <TableCell>{row.userName || "—"}</TableCell>
                      <TableCell align="right">{row.ticketId || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {report.details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {i18n.t("forbiddenWordsReport.noOccurrences")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default ForbiddenWordsReport;
